/**
 * WhatsAppAccountSetupPage — `/whatsapp/accounts/:id/setup`
 *
 * Wizard de configuración que se abre después de vincular un número.
 * 5 pasos secuenciales:
 *  1. Perfil de negocio
 *  2. Mensaje de prueba
 *  3. Auto-respuestas
 *  4. Asignar a campañas
 *  5. Plan de warm-up + activación
 *
 * Progress bar arriba muestra qué pasos completaste. Se puede saltar entre
 * pasos completados libremente, pero los pendientes los hacés en orden.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Loader2,
    CheckCircle2,
    Building2,
    MessageSquare,
    MessageCircle,
    Target,
    Shield,
    Lock,
} from "lucide-react";
import WhatsAppSubNav from "../components/WhatsAppSubNav";
import BusinessProfileStep from "../components/setup/BusinessProfileStep";
import TestMessageStep from "../components/setup/TestMessageStep";
import AutoReplyStep from "../components/setup/AutoReplyStep";
import ObjectivesAssignmentStep from "../components/setup/ObjectivesAssignmentStep";
import WarmupPlanStep from "../components/setup/WarmupPlanStep";
import { whatsappAccountsApi } from "../api";
import type { WhatsAppAccount, WhatsAppSetupProgress } from "../types";

type StepKey =
    | "businessProfile"
    | "testMessage"
    | "autoReplyConfigured"
    | "objectivesAssigned"
    | "warmupAcknowledged";

interface StepDef {
    key: StepKey;
    label: string;
    icon: typeof Building2;
}

const STEPS: StepDef[] = [
    { key: "businessProfile", label: "Perfil de negocio", icon: Building2 },
    { key: "testMessage", label: "Mensaje de prueba", icon: MessageSquare },
    { key: "autoReplyConfigured", label: "Auto-respuestas", icon: MessageCircle },
    { key: "objectivesAssigned", label: "Asignar a campañas", icon: Target },
    { key: "warmupAcknowledged", label: "Plan de warm-up", icon: Shield },
];

export default function WhatsAppAccountSetupPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [account, setAccount] = useState<WhatsAppAccount | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState<StepKey>("businessProfile");

    /* Load account */
    useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            const res = await whatsappAccountsApi.getById(id);
            if (!res.success || !res.data) {
                setError(res.error || "No se pudo cargar la cuenta");
            } else {
                setAccount(res.data);
                // Saltamos al primer step incompleto
                const progress = res.data.setupProgress || ({} as WhatsAppSetupProgress);
                const firstIncomplete = STEPS.find((s) => !progress[s.key]);
                if (firstIncomplete) setCurrentStep(firstIncomplete.key);
            }
            setLoading(false);
        })();
    }, [id]);

    /* Callback que cada step llama al completarse */
    const handleStepComplete = (updated: WhatsAppAccount) => {
        setAccount(updated);
        const progress = updated.setupProgress || ({} as WhatsAppSetupProgress);
        const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
        const nextStep = STEPS.slice(currentIdx + 1).find((s) => !progress[s.key]);
        if (nextStep) {
            setCurrentStep(nextStep.key);
        } else if (updated.activationStatus !== "unconfigured") {
            // Wizard completo + activación lista → volvé al listado
            setTimeout(() => navigate("/whatsapp/accounts"), 1500);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
                <Loader2 size={18} className="animate-spin mr-2" />
                Cargando cuenta…
            </div>
        );
    }
    if (error || !account) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3">
                <p className="text-sm text-red-600">
                    {error || "Cuenta no encontrada"}
                </p>
                <Link
                    to="/whatsapp/accounts"
                    className="text-xs text-violet-600 underline"
                >
                    Volver a cuentas
                </Link>
            </div>
        );
    }

    const progress = account.setupProgress || ({} as WhatsAppSetupProgress);
    const completedCount = STEPS.filter((s) => progress[s.key]).length;
    const isFullyConfigured = completedCount === STEPS.length;

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-5xl space-y-6">
                <WhatsAppSubNav active="accounts" />

                {/* Back link */}
                <Link
                    to="/whatsapp/accounts"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a cuentas
                </Link>

                {/* Hero */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
                                Setup de cuenta
                            </span>
                            <h1 className="text-2xl font-bold text-slate-900 mt-2 tracking-tight">
                                {account.displayName}
                            </h1>
                            <p className="text-xs text-slate-500 font-mono mt-0.5">
                                {account.phoneNumber}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-slate-500">Progreso</p>
                            <p className="text-2xl font-bold text-slate-900 tabular-nums">
                                {completedCount}/{STEPS.length}
                            </p>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className={`h-full transition-all duration-500 ${
                                isFullyConfigured
                                    ? "bg-emerald-500"
                                    : "bg-gradient-to-r from-violet-400 to-violet-600"
                            }`}
                            style={{
                                width: `${(completedCount / STEPS.length) * 100}%`,
                            }}
                        />
                    </div>
                </div>

                {/* Layout: sidebar steps + step content */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* Steps sidebar */}
                    <nav className="md:col-span-4 space-y-1">
                        {STEPS.map((s, i) => {
                            const isDone = progress[s.key] === true;
                            const isCurrent = s.key === currentStep;
                            // Bloqueamos pasos futuros si los anteriores no están listos
                            const previousAllDone = STEPS.slice(0, i).every(
                                (prev) => progress[prev.key] === true,
                            );
                            const isLocked = !isDone && !isCurrent && !previousAllDone;
                            const Icon = s.icon;

                            return (
                                <button
                                    key={s.key}
                                    disabled={isLocked}
                                    onClick={() => setCurrentStep(s.key)}
                                    className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                                        isCurrent
                                            ? "border-violet-500 bg-violet-50/50"
                                            : isDone
                                              ? "border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50/50"
                                              : isLocked
                                                ? "border-slate-100 bg-slate-50/50 opacity-50 cursor-not-allowed"
                                                : "border-slate-200 bg-white hover:bg-slate-50"
                                    }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            isDone
                                                ? "bg-emerald-100 text-emerald-700"
                                                : isCurrent
                                                  ? "bg-violet-100 text-violet-700"
                                                  : "bg-slate-100 text-slate-400"
                                        }`}
                                    >
                                        {isDone ? (
                                            <CheckCircle2 size={15} />
                                        ) : isLocked ? (
                                            <Lock size={13} />
                                        ) : (
                                            <Icon size={15} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                            Paso {i + 1}
                                        </p>
                                        <p
                                            className={`text-sm font-semibold truncate ${
                                                isCurrent
                                                    ? "text-violet-900"
                                                    : isDone
                                                      ? "text-emerald-900"
                                                      : "text-slate-700"
                                            }`}
                                        >
                                            {s.label}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}

                        {isFullyConfigured && (
                            <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                                <div className="flex items-center gap-2 mb-1">
                                    <CheckCircle2
                                        size={14}
                                        className="text-emerald-600"
                                    />
                                    <p className="text-xs font-bold text-emerald-800">
                                        Setup completo
                                    </p>
                                </div>
                                <p className="text-[11px] text-emerald-700 leading-relaxed">
                                    La cuenta queda en warm-up por 14 días. Te llevamos al
                                    listado en unos segundos…
                                </p>
                            </div>
                        )}
                    </nav>

                    {/* Step content */}
                    <div className="md:col-span-8">
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                            {currentStep === "businessProfile" && (
                                <BusinessProfileStep
                                    account={account}
                                    onComplete={handleStepComplete}
                                />
                            )}
                            {currentStep === "testMessage" && (
                                <TestMessageStep
                                    account={account}
                                    onComplete={handleStepComplete}
                                />
                            )}
                            {currentStep === "autoReplyConfigured" && (
                                <AutoReplyStep
                                    account={account}
                                    onComplete={handleStepComplete}
                                />
                            )}
                            {currentStep === "objectivesAssigned" && (
                                <ObjectivesAssignmentStep
                                    account={account}
                                    onComplete={handleStepComplete}
                                />
                            )}
                            {currentStep === "warmupAcknowledged" && (
                                <WarmupPlanStep
                                    account={account}
                                    onComplete={handleStepComplete}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
